import { useGetPracticeMineralFertilizers, useGetPracticeOrganicFertilizers } from "utils";

import { PRACTICE_SLUG, ZERO_UUID } from "models";

/**
 * Custom hook that provides helper functions for fertilizer practices
 * Encapsulates data fetching and business logic to avoid prop drilling
 */
export const useFertilizerHelpers = () => {
  const { data: mineralFertilizers } = useGetPracticeMineralFertilizers({ enabled: true });
  const { data: organicFertilizers } = useGetPracticeOrganicFertilizers({ enabled: true });

  // Helper function to get fertilizer name
  const getFertilizerName = (type: string, assignedPracticeId: string, fallbackText = "Unknown") => {
    if (assignedPracticeId === ZERO_UUID) {
      if (type === PRACTICE_SLUG.MINERAL_FERTILIZERS) return "No mineral fertilizer applied";
      if (type === PRACTICE_SLUG.ORGANIC_FERTILIZERS) return "No organic fertilizer applied";
      return "No fertilizer applied";
    }

    const fertilizerList = type === PRACTICE_SLUG.MINERAL_FERTILIZERS ? mineralFertilizers : organicFertilizers;
    return fertilizerList?.find((f) => f.id === assignedPracticeId)?.name ?? fallbackText;
  };

  // Helper function to get fertilizer N-Value
  const getFertilizerNValue = (fertilizer: { type: string; assignedPracticeId: string; nValue?: number | null }) => {
    if (fertilizer.assignedPracticeId === ZERO_UUID) {
      return null;
    }

    if (fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS) {
      const mineralFertilizer = mineralFertilizers?.find((f) => f.id === fertilizer.assignedPracticeId);
      return mineralFertilizer?.nValue ?? null;
    }

    if (fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS) {
      return fertilizer.nValue ?? null;
    }

    return null;
  };

  return {
    getFertilizerName,
    getFertilizerNValue,
    mineralFertilizers,
    organicFertilizers,
  };
};
